import React, { useState, useEffect, useRef, useCallback } from "react";
import Service from "../api/service";
import DescriptionIcon from "@mui/icons-material/Description";
import { createPortal } from "react-dom";
import { showErrorToast } from "./toasts/ToastNotification";
import requestQueue from "../utility/requestQueue";

// Simple global cache to persist deliverables across unmounts/remounts (virtualization)
export const deliverablesCache = new Map();

/**
 * Force clear the cache for a specific task or all tasks.
 * Useful when new deliverables are uploaded.
 */
export const clearDeliverablesCache = (taskId) => {
  if (taskId) {
    deliverablesCache.delete(taskId);
    // Dispatch event so active components can re-fetch
    window.dispatchEvent(new CustomEvent("refreshDeliverables", { detail: { taskId } }));
  } else {
    deliverablesCache.clear();
  }
};

const DeliverablesDots = ({ taskId, x, y, setTooltip, zoomRatio }) => {
  const [deliverables, setDeliverables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const containerRef = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // --- Zoom Guard Logic ---
  // When zoomed out past 0.3, we don't render or fetch anything to save performance/API budget.
  const isZoomedOut = zoomRatio !== undefined && zoomRatio < 0.3;

  useEffect(() => {
    if (isZoomedOut) return;

    // Reset state when taskId changes
    const cached = deliverablesCache.get(taskId);
    if (cached) {
      setDeliverables(cached);
      setLoading(false);
      setHasFetched(true);
      return;
    }

    setDeliverables([]);
    setLoading(true);
    setHasFetched(false);
  }, [taskId, isZoomedOut]);

  const fetchDeliverables = useCallback(() => {
    if (isZoomedOut || (deliverables.length > 0 && hasFetched)) return;
    
    setLoading(true);
    
    // Use the requestQueue to throttle and handle retries
    requestQueue.add(() => Service.EnoviaService().getTaskDeliverables(taskId))
      .then((res) => {
        if (!isMounted.current) return;
        const data = res?.data?.data || res?.data || [];
        const items = Array.isArray(data) ? data : [];
        deliverablesCache.set(taskId, items);
        setDeliverables(items);
        setHasFetched(true);
      })
      .catch((err) => {
        if (!isMounted.current) return;
        console.error("Failed to fetch deliverables", err);
        
        // If it's a permanent error (not a 429 which the queue retries),
        // we mark it as "fetched" with an empty list to avoid infinite retries.
        if (err?.response?.status !== 429) {
          deliverablesCache.set(taskId, []);
          setHasFetched(true);
        }
      })
      .finally(() => {
        if (isMounted.current) setLoading(false);
      });
  }, [taskId, deliverables.length, isZoomedOut, hasFetched]);

  useEffect(() => {
    if (isZoomedOut) return;

    const handleRefresh = (e) => {
      if (e.detail && e.detail.taskId === taskId) {
        fetchDeliverables();
      }
    };

    window.addEventListener("refreshDeliverables", handleRefresh);
    return () => window.removeEventListener("refreshDeliverables", handleRefresh);
  }, [taskId, fetchDeliverables, isZoomedOut]);

  // Replacement for IntersectionObserver:
  // Since the component is now conditionally rendered by the Zoom Guard,
  // we can simply fetch when it mounts if not already fetched.
  useEffect(() => {
    if (isZoomedOut) return;

    if (!hasFetched) {
      const cached = deliverablesCache.get(taskId);
      if (cached) {
        setDeliverables(cached);
        setLoading(false);
        setHasFetched(true);
      } else {
        fetchDeliverables();
      }
    }
  }, [taskId, hasFetched, fetchDeliverables, isZoomedOut]);

  const [hoveredDeliv, setHoveredDeliv] = useState(null);
  const hideTimeout = useRef(null);

  if (isZoomedOut) return null;

  const handleMouseEnter = (e, deliv) => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setHoveredDeliv({ deliv, rect });
    
    // Hide the main node tooltip so it doesn't overlap/interfere
    if (setTooltip) {
      setTooltip((t) => (t ? { ...t, visible: false } : null));
    }
  };

  const handleMouseLeave = () => {
    hideTimeout.current = setTimeout(() => {
      setHoveredDeliv(null);
    }, 200);
  };

  const handleDownload = (e, deliv) => {
    e.stopPropagation();
    const delivId = deliv.id || deliv.objectId;
    if (!delivId) return;

    Service.EnoviaService()
      .getDocumentDownloadTicket(delivId, {
        headers: { Accept: "application/json" },
      })
      .then((res) => {
        const dataElements = res.data?.data?.[0]?.dataelements;
        if (dataElements && dataElements.ticketURL) {
          const ticketUrl = dataElements.ticketURL;
          const fileName = dataElements.fileName || deliv.title || "download";

          // Fetch the file directly using the ticket URL
          fetch(ticketUrl)
            .then((response) => {
              if (!response.ok) throw new Error("Network response was not ok");
              return response.blob();
            })
            .then((blob) => {
              // Create local URL and trigger download
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.setAttribute("download", fileName);
              document.body.appendChild(link);
              link.click();

              // Cleanup
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            })
            .catch((err) => {
              console.error("Failed to download blob from ticket:", err);
              // Fallback to opening the ticket URL in a new tab
              window.open(ticketUrl, "_blank");
            });
        } else {
          console.error("No ticket URL found in response", res.data);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch download ticket:", err);
        showErrorToast("Failed to start download. Please check your connection or permissions.");
      });
  };

  if (loading) {
    return (
      <foreignObject ref={containerRef} x={x - 20} y={y - 15} width="80" height="20">
        <div style={{ width: "100%", height: "100%" }} />
      </foreignObject>
    );
  }

  if (deliverables.length === 0) return null;

  return (
    <foreignObject ref={containerRef} x={x - 20} y={y - 15} width="80" height="20">
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "4px",
          width: "100%",
          height: "100%",
        }}
      >
        {(() => {
          try {
            return deliverables.slice(0, 3).map((deliv, idx) => {
              const safeDeliv = deliv || {};
              const uniqueKey = safeDeliv.id ? `${safeDeliv.id}-${idx}` : `dot-${idx}`;
              return (
                <DescriptionIcon
                  key={uniqueKey}
                  style={{
                    fontSize: "14px",
                    color: "#555",
                    cursor: "pointer",
                    flexShrink: 0
                  }}
                  onMouseEnter={(e) => handleMouseEnter(e, safeDeliv)}
                  onMouseLeave={handleMouseLeave}
                  onClick={(e) => e.stopPropagation()}
                />
              );
            });
          } catch (e) {
            console.error("Error rendering deliverables:", e);
            return null;
          }
        })()}
        {deliverables.length > 3 && (
          <span style={{ fontSize: "12px", fontWeight: "bold", color: "#555", letterSpacing: "0px", marginBottom: "6px" }}>...</span>
        )}

        {hoveredDeliv &&
          createPortal(
            <div
              style={{
                position: "fixed",
                top: hoveredDeliv.rect.top - 36,
                left: hoveredDeliv.rect.left - 20,
                background: "white",
                color: "#333",
                padding: "4px 8px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                fontSize: "12px",
                zIndex: 999999,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                pointerEvents: "auto",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
              onMouseEnter={() => {
                if (hideTimeout.current) clearTimeout(hideTimeout.current);
              }}
              onMouseLeave={handleMouseLeave}
              onClick={(e) => e.stopPropagation()}
            >
              <span 
                style={{ 
                  whiteSpace: "nowrap", 
                  color: "#1976d2", 
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontWeight: 500
                }}
                onClick={(e) => handleDownload(e, hoveredDeliv.deliv)}
                title="Click to Download"
              >
                {hoveredDeliv.deliv?.title ||
                  hoveredDeliv.deliv?.dataelements?.title ||
                  hoveredDeliv.deliv?.name || 
                  "Deliverable"}
              </span>
            </div>,
            document.body
          )}
      </div>
    </foreignObject>
  );
};

export default DeliverablesDots;
